import { ref, computed, watch, type WatchStopHandle } from 'vue';
import type {
  BattleplanEntry,
  BattlepackBundle,
  IBattleplanMap,
  IBattleplan,
  BattlePack,
} from '@scrolldex/shared';
import { BUNDLE_COLLECTION_KEYS } from '@scrolldex/shared';
import { useWorkspace } from './workspace.js';

// A library entry is a stored bundle (its content) plus library bookkeeping. It
// derives its content fields from BattlepackBundle so any new bundle collection
// is persisted automatically — there are no hand-listed content fields here.
// studioVersion is export-only (written by exportBundle), so it's dropped.
export type LibraryEntry = Omit<BattlepackBundle, 'studioVersion'> & {
  id: string;
  name: string;
  updatedAt: number;
};

// Legacy library entries (pre-bundle) stored maps and authored in two parallel
// records. Kept only for migrating persisted localStorage on init. Content
// fields are inherited from LibraryEntry (Partial) so new collections migrate
// without edits here; only the pre-bundle maps/authored shape is spelled out.
type LegacyLibraryEntry = Partial<LibraryEntry> & {
  id: string;
  name: string;
  battlePack: BattlePack;
  updatedAt: number;
  maps?: Record<string, IBattleplanMap>;
  authored?: Record<string, IBattleplan>;
};

// Copy a bundle's content (battlePack + battleplans + every registered
// collection) onto a library entry, clearing collections the source omits. This
// is the single place library state mirrors bundle content — it iterates
// BUNDLE_COLLECTION_KEYS, so new collections flow through with no change here.
function assignBundleContent(target: LibraryEntry, source: BattlepackBundle): void {
  if (source.battlePack) target.battlePack = source.battlePack;
  target.battleplans = source.battleplans;
  for (const key of BUNDLE_COLLECTION_KEYS) {
    (target as unknown as Record<string, unknown>)[key] = source[key];
  }
}

const LIB_KEY = 'studio:library';
const ACTIVE_KEY = 'studio:activeId';
const PLAN_KEY = 'studio:activePlan';

const entries = ref<LibraryEntry[]>([]);
const activeId = ref<string | null>(null);

let writebackStarted = false;
let suspendWriteback = false;
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let stopWritebackWatchers: WatchStopHandle[] = [];
let stopGlobalWritebackListeners: (() => void) | null = null;

const ws = useWorkspace();

function genId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

// Fold a legacy {maps, authored} entry into the unified {battleplans} shape.
// A modern entry (already has battleplans) is carried through wholesale via
// spread, so every content collection — including ones added later — survives a
// reload. Only the genuinely legacy pre-bundle shape is reconstructed.
function migrateEntry(e: LegacyLibraryEntry): LibraryEntry {
  if (e.battleplans) {
    const { maps: _maps, authored: _authored, ...rest } = e;
    return { ...rest, battleplans: e.battleplans } as LibraryEntry;
  }
  const battleplans: Record<string, BattleplanEntry> = {};
  for (const [name, map] of Object.entries(e.maps ?? {})) battleplans[name] = { map };
  for (const [name, authored] of Object.entries(e.authored ?? {})) {
    battleplans[name] = { ...(battleplans[name] ?? {}), authored };
  }
  const { maps: _maps, authored: _authored, ...rest } = e;
  return { ...rest, battleplans } as LibraryEntry;
}

function persist() {
  localStorage.setItem(LIB_KEY, JSON.stringify(entries.value));
  localStorage.setItem(ACTIVE_KEY, activeId.value ?? '');
}

function find(id: string | null): LibraryEntry | undefined {
  return id ? entries.value.find((e) => e.id === id) : undefined;
}

export function deriveEntryName(filename: string): string {
  return filename.replace(/\.json$/i, '').replace(/_authored$/i, '');
}

// Upsert a library entry by name; returns the (existing or new) entry.
function upsertByName(name: string, battlePack: BattlePack): LibraryEntry {
  let entry = entries.value.find((e) => e.name === name);
  if (!entry) {
    entry = { id: genId(), name, battlePack, battleplans: {}, updatedAt: Date.now() };
    entries.value.push(entry);
  }
  entry.battlePack = battlePack;
  return entry;
}

function doSync() {
  const entry = find(activeId.value);
  if (!entry) return;
  assignBundleContent(entry, ws.snapshotBundle());
  entry.updatedAt = Date.now();
  persist();
}

function syncActiveFromWorkspaceImpl() {
  if (suspendWriteback) return;
  doSync();
}

function stopWriteback() {
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  for (const stop of stopWritebackWatchers) stop();
  stopWritebackWatchers = [];
  stopGlobalWritebackListeners?.();
  stopGlobalWritebackListeners = null;
  writebackStarted = false;
}

export function useLibrary() {
  return {
    entries: computed(() => entries.value),
    activeId: computed(() => activeId.value),
    activeEntry: computed(() => find(activeId.value) ?? null),

    // test-only: clear in-memory state (does NOT touch localStorage)
    reset() {
      entries.value = [];
      activeId.value = null;
      stopWriteback();
      suspendWriteback = false;
    },

    init() {
      const raw = localStorage.getItem(LIB_KEY);
      const parsed = raw ? (JSON.parse(raw) as LegacyLibraryEntry[]) : [];
      entries.value = parsed.map(migrateEntry);
      const saved = localStorage.getItem(ACTIVE_KEY) || null;
      const savedPlan = localStorage.getItem(PLAN_KEY) || null;
      if (saved && entries.value.some((e) => e.id === saved)) {
        this.setActive(saved); // loads the pack; selects its first plan
        // Restore the previously-selected battleplan if it still exists.
        if (savedPlan && ws.planNames.value.includes(savedPlan)) {
          ws.selectPlan(savedPlan);
        }
      }
      this.startWriteback();
    },

    syncActiveFromWorkspace() {
      doSync();
    },

    startWriteback() {
      if (writebackStarted) return;
      writebackStarted = true;
      // Watch the whole serialized bundle rather than a hand-listed set of refs:
      // snapshotBundle() reads every content field, so the getter's reactive deps
      // automatically include any new collection — no watch entry to add. Each
      // call returns a fresh object, so the watcher fires whenever any tracked
      // content changes, then debounces the persist.
      stopWritebackWatchers.push(
        watch(
          () => ws.snapshotBundle(),
          () => {
            if (syncTimer) clearTimeout(syncTimer);
            syncTimer = setTimeout(syncActiveFromWorkspaceImpl, 300);
          },
        ),
      );
      // Remember the selected battleplan so it's restored on reload.
      stopWritebackWatchers.push(
        watch(
          () => ws.currentPlan.value,
          (name) => localStorage.setItem(PLAN_KEY, name ?? ''),
        ),
      );
      // Hard-flush on focus/visibility loss and close so a reload, app switch,
      // or navigation during the 300ms debounce window can't drop the most
      // recent edits. window 'blur' covers leaving the window/tab when it does
      // not also go hidden (e.g. switching to another app or browser window).
      if (typeof window !== 'undefined') {
        const flush = () => {
          if (!suspendWriteback) doSync();
        };
        const flushOnVisibilityChange = () => {
          if (document.visibilityState === 'hidden') flush();
        };
        window.addEventListener('beforeunload', flush);
        window.addEventListener('blur', flush);
        document.addEventListener('visibilitychange', flushOnVisibilityChange);
        stopGlobalWritebackListeners = () => {
          window.removeEventListener('beforeunload', flush);
          window.removeEventListener('blur', flush);
          document.removeEventListener('visibilitychange', flushOnVisibilityChange);
        };
      }
    },

    createPack(battlePack: BattlePack, name: string): LibraryEntry {
      const entry: LibraryEntry = {
        id: genId(),
        name,
        battlePack,
        battleplans: {},
        updatedAt: Date.now(),
      };
      entries.value.push(entry);
      persist();
      this.setActive(entry.id);
      return entry;
    },

    setActive(id: string) {
      const entry = find(id);
      if (!entry) return;
      // Drop any pending debounced sync so it can't fire against the new bundle
      // after we swap.
      if (syncTimer) {
        clearTimeout(syncTimer);
        syncTimer = null;
      }
      // Flush pending edits to the OUTGOING entry before we replace the
      // workspace — loadBundle() discards in-memory state and the 300ms autosave
      // debounce may not have fired yet, so a switch must be a hard save point
      // (duplicatePack/export already flush the same way). Only when actually
      // switching bundles: re-activating the current id (e.g. applyBundleFile
      // overwriting the active entry) must NOT flush, or the stale workspace
      // would clobber the just-applied data. doSync() targets the current
      // activeId, so run it before reassigning.
      if (id !== activeId.value) doSync();
      activeId.value = id;
      suspendWriteback = true;
      // A LibraryEntry is a superset of BattlepackBundle (adds id/name/updatedAt,
      // which loadBundle ignores), so it loads directly — no field enumeration.
      ws.loadBundle(entry);
      persist();
      // release on the next macrotask so the watcher's debounced fire is skipped
      setTimeout(() => {
        suspendWriteback = false;
      }, 0);
    },

    deletePack(id: string) {
      entries.value = entries.value.filter((e) => e.id !== id);
      if (activeId.value === id) {
        const next = entries.value[0];
        if (next) {
          this.setActive(next.id);
        } else {
          activeId.value = null;
          ws.reset();
        }
      }
      persist();
    },

    renamePack(id: string, name: string) {
      const entry = find(id);
      if (!entry) return;
      entry.name = name;
      entry.updatedAt = Date.now();
      persist();
    },

    // Duplicate a battlepack (deep-copying its battleplans) and make the copy
    // active. Flushes pending edits first when duplicating the active pack so the
    // copy reflects the latest in-editor state. Returns the new entry.
    duplicatePack(id: string): LibraryEntry | null {
      if (id === activeId.value) doSync();
      const source = find(id);
      if (!source) return null;
      const base = `${source.name} copy`;
      let name = base;
      for (let n = 2; entries.value.some((e) => e.name === name); n++) name = `${base} ${n}`;
      // Deep-clone the whole source entry (covers every content collection), then
      // override the bookkeeping fields. No per-field enumeration to keep in sync.
      const entry: LibraryEntry = {
        ...(JSON.parse(JSON.stringify(source)) as LibraryEntry),
        id: genId(),
        name,
        updatedAt: Date.now(),
      };
      entries.value.push(entry);
      persist();
      this.setActive(entry.id);
      return entry;
    },

    findByName(name: string): LibraryEntry | null {
      return entries.value.find((e) => e.name === name) ?? null;
    },

    // Apply a loaded bundle (already normalized from any supported file shape via
    // toBundle) into a library entry by name, then make it active.
    applyBundleFile(bundle: BattlepackBundle, name: string) {
      const entry = upsertByName(name, bundle.battlePack);
      assignBundleContent(entry, bundle);
      entry.updatedAt = Date.now();
      persist();
      this.setActive(entry.id);
    },
  };
}
